import { useOrganizationPartners } from '@/api'
import {
  FileInput,
  validateFileSelection,
  type FileUploadModalProps
} from '@/components/file-uploader'
import { useUploadableDocumentTypes } from '@/helpers/permission-helpers'
import { useAuthContext } from '@/hooks/use-auth-context'
import {
  ActionIcon,
  Button,
  Checkbox,
  Group,
  Modal,
  Progress,
  ScrollArea,
  Select,
  Stack,
  Text,
  Tooltip
} from '@mantine/core'
import { useCallback, useEffect, useState } from 'react'
import { toastError, toastInfo, toastSuccess } from '../toast/toast'
import { useBulkFileUpload } from './hooks/use-bulk-file-upload'
import { useDocumentType } from './hooks/use-document-type'
import { useFileUpload } from './hooks/use-file-upload'
import { UploadStatus } from './types/upload-types'
import { Icon } from '@/components/icon'
import { faTrashCan as faTrashCanRegular } from '@fortawesome/free-regular-svg-icons'
import {
  faCheckDouble,
  faClockRotateLeft,
  faXmark
} from '@fortawesome/free-solid-svg-icons'

const MULTI_PARTNER_VALUE = '__multi_partner__'

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

const DOCUMENT_TYPES = [
  { value: 'SPA', label: 'Special Pricing Agreement' },
  { value: 'POSTransactions', label: 'POS Transaction' },
  { value: 'Pricesheet', label: 'Price Sheet' },
  { value: 'ClaimResponse', label: 'Claim Response' }
]

export default function FileUploadModal({
  isOpen = false,
  setIsOpen
}: FileUploadModalProps) {
  const { organization } = useAuthContext()
  const { uploading, setUploading, files, setFiles, handleFileChange } =
    useFileUpload()
  const { documentType, setDocumentType } = useDocumentType()
  const { uploadFiles } = useBulkFileUpload()
  const orgType = organization?.OrganizationType
  // Only the types the API lets this caller upload.
  const uploadableDocumentTypes = useUploadableDocumentTypes()

  const { data: partners } = useOrganizationPartners(organization?.ID)
  const [selectedPartner, setSelectedPartner] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState<
    Map<string, UploadStatus>
  >(new Map())
  const [isHeadless, setIsHeadless] = useState(false)

  const fileValidation =
    files.length > 0
      ? validateFileSelection(files)
      : { valid: true, errors: [] as string[] }

  const totalSize = files.reduce((sum, file) => sum + file.size, 0)

  const uploadStatuses = Array.from(uploadProgress.values())
  const doneCount = uploadStatuses.filter(
    (s) => s.status === 'success' || s.status === 'error'
  ).length
  const overallProgress =
    uploadStatuses.length > 0
      ? uploadStatuses.reduce(
          (sum, s) => sum + (s.status === 'error' ? 100 : s.progress),
          0
        ) / uploadStatuses.length
      : 0

  // Convert partners to Select options, prepend Multi-Partner for POS uploads
  const basePartnerOptions =
    partners?.map((partner) => ({
      value: partner.ID,
      label: partner.Name
    })) || []

  const partnerOptions =
    documentType === 'POSTransactions'
      ? [
          { value: MULTI_PARTNER_VALUE, label: 'Multi-Partner' },
          ...basePartnerOptions
        ]
      : basePartnerOptions

  const isMultiPartner = selectedPartner === MULTI_PARTNER_VALUE

  // Reset isHeadless and Multi-Partner selection when document type changes away from POSTransactions
  useEffect(() => {
    if (documentType !== 'POSTransactions') {
      queueMicrotask(() => setIsHeadless(false))
      if (selectedPartner === MULTI_PARTNER_VALUE) {
        queueMicrotask(() => setSelectedPartner(null))
      }
    }
  }, [documentType, selectedPartner])

  const handleClose = useCallback(() => {
    if (!uploading) {
      setIsOpen(false)
      setFiles([])
      setSelectedPartner(null)
      setDocumentType(null)
      setUploadProgress(new Map())
      setIsHeadless(false)
    }
  }, [uploading, setIsOpen, setFiles, setDocumentType])

  // A changed selection starts fresh: the last upload's failed rows no longer apply.
  const clearResults = () => setUploadProgress(new Map())

  const handleRemoveFile = (index: number) => {
    clearResults()
    setFiles((files) => files.filter((_, i) => i !== index))
  }

  const handleSubmitForm = async (event: React.FormEvent) => {
    event.preventDefault()

    // Validate file selection
    const validation = validateFileSelection(files)
    if (!validation.valid) {
      toastError(validation.errors.join(' ') || 'Validation failed')
      return
    }

    if (!selectedPartner || !documentType || !orgType) {
      toastError('Please select a partner organization and document type')
      return
    }

    setUploading(true)

    // Initialize progress tracking
    const progressMap = new Map<string, UploadStatus>()
    files.forEach((file, index) => {
      progressMap.set(`${index}-${file.name}`, {
        fileName: file.name,
        status: 'pending',
        progress: 0
      })
    })
    setUploadProgress(progressMap)

    try {
      // Upload all files using React Query hook
      const results = await uploadFiles({
        files,
        partnerOrganizationId: isMultiPartner ? undefined : selectedPartner,
        documentType,
        userOrgType: orgType,
        isHeadless: documentType === 'POSTransactions' ? isHeadless : undefined,
        onProgress: (fileKey, status) => {
          // Update progress callback
          setUploadProgress((prev) => {
            const updated = new Map(prev)
            updated.set(fileKey, status)
            return updated
          })
        }
      })

      // Handle results
      const successCount = results.filter(
        (r) => r.status === 'fulfilled'
      ).length
      const failureCount = results.filter((r) => r.status === 'rejected').length

      if (failureCount === 0) {
        toastSuccess(`All ${successCount} files uploaded successfully`)
        handleClose()
      } else {
        toastInfo(`${successCount} succeeded, ${failureCount} failed`)
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Upload failed'
      toastError(errorMessage)
      console.error('Error uploading files:', error)
    }
    setUploading(false)
  }

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      title="Upload Files"
      size="lg"
      closeOnClickOutside={!uploading}
      closeOnEscape={!uploading}
      withCloseButton={!uploading}
    >
      <form onSubmit={handleSubmitForm}>
        <Stack gap="md">
          <div>
            <FileInput
              handleFileChange={(event) => {
                handleFileChange(event)
                clearResults()
              }}
            />
            {documentType === 'Pricesheet' && (
              <Text size="xs" c="dimmed" mt="xs">
                A two-file price sheet needs both files in one upload: the
                Catalog (book file) and the Discount Schedule (netted file).
              </Text>
            )}
            {files.length > 0 && (
              <div>
                <Group justify="space-between" align="center">
                  <Text size="sm" fw={500} c="dimmed">
                    Manage files:
                  </Text>
                  <Button
                    variant="subtle"
                    color="red"
                    size="xs"
                    onClick={() => {
                      setFiles([])
                      clearResults()
                    }}
                    disabled={uploading}
                  >
                    Remove all files
                  </Button>
                </Group>
                <ScrollArea.Autosize
                  mah={160}
                  type={files.length > 4 ? 'always' : 'auto'}
                  offsetScrollbars
                >
                  <div className="space-y-1">
                    {files.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between rounded bg-gray-50 p-2 text-sm"
                      >
                        <Text size="sm" className="flex-1 truncate">
                          {file.name}
                        </Text>
                        <Tooltip label="Remove file">
                          <ActionIcon
                            aria-label={`Remove ${file.name}`}
                            color="red"
                            variant="subtle"
                            onClick={() => handleRemoveFile(index)}
                            disabled={uploading}
                            size="sm"
                          >
                            <Icon icon={faTrashCanRegular} />
                          </ActionIcon>
                        </Tooltip>
                      </div>
                    ))}
                  </div>
                </ScrollArea.Autosize>
                <div className="mt-2">
                  <Text size="sm" fw={600}>
                    Selected: {files.length} file
                    {files.length !== 1 ? 's' : ''} ({formatFileSize(totalSize)}
                    )
                  </Text>
                  {!fileValidation.valid &&
                    fileValidation.errors.map((err) => (
                      <Text key={err} size="sm" c="red">
                        {err}
                      </Text>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Failed files stay listed after the upload, so the user can read why. */}
          {uploadProgress.size > 0 &&
            (uploading || uploadStatuses.some((s) => s.status === 'error')) && (
              <div className="mb-4 space-y-2">
                <Group justify="space-between" align="center">
                  <Text size="sm" fw={500} c="dimmed">
                    Upload Progress:
                  </Text>
                  <Text size="sm" fw={600}>
                    {doneCount} of {uploadStatuses.length} done
                  </Text>
                </Group>
                <Progress value={overallProgress} size="md" animated />
                <ScrollArea.Autosize mah={240} offsetScrollbars>
                  <div className="space-y-2">
                    {Array.from(uploadProgress.entries()).map(
                      ([fileKey, status]) => (
                        <div key={fileKey} className="space-y-1">
                          <div className="flex items-center justify-between">
                            <Text size="xs" className="flex-1 truncate">
                              {status.fileName}
                            </Text>
                            <span className="ml-2 whitespace-nowrap">
                              {status.status === 'success' && (
                                <Icon
                                  icon={faCheckDouble}
                                  size={16}
                                  color="green"
                                />
                              )}
                              {status.status === 'error' && (
                                <Icon icon={faXmark} size={16} color="red" />
                              )}
                              {status.status === 'uploading' && (
                                <Text size="xs" c="blue">
                                  {status.progress}%
                                </Text>
                              )}
                              {status.status === 'pending' && (
                                <Icon
                                  icon={faClockRotateLeft}
                                  size={14}
                                  color="gray"
                                />
                              )}
                            </span>
                          </div>
                          {status.status === 'uploading' && (
                            <Progress
                              value={status.progress}
                              size="xs"
                              animated
                            />
                          )}
                          {status.error && (
                            <Text size="xs" c="red">
                              {status.error}
                            </Text>
                          )}
                        </div>
                      )
                    )}
                  </div>
                </ScrollArea.Autosize>
              </div>
            )}

          <Select
            label="Document Type"
            placeholder="Select a document type"
            data={DOCUMENT_TYPES.filter((type) =>
              uploadableDocumentTypes.includes(type.value)
            )}
            value={documentType}
            onChange={setDocumentType}
            searchable
            required
            withAsterisk
            disabled={uploading}
          />

          {documentType === 'POSTransactions' && (
            <div>
              <Checkbox
                label="Headless file (no header row)"
                checked={isHeadless}
                onChange={(event) => setIsHeadless(event.currentTarget.checked)}
                disabled={uploading}
              />
            </div>
          )}

          <Select
            label="Partner Organization"
            placeholder="Select a partner organization"
            data={partnerOptions}
            value={selectedPartner}
            onChange={setSelectedPartner}
            searchable
            required
            withAsterisk
            disabled={uploading}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={handleClose} disabled={uploading}>
              Cancel
            </Button>
            <Button
              data-testid="file-uploader-submit"
              type="submit"
              loading={uploading}
              disabled={
                uploading ||
                !selectedPartner ||
                !documentType ||
                files.length === 0 ||
                !fileValidation.valid
              }
            >
              {uploading
                ? 'Uploading...'
                : `Upload ${files.length} file${files.length !== 1 ? 's' : ''}`}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}
